/**
 * Club Management - Platform admin interface for managing all clubs
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Trophy, Calendar, MapPin, Mail, Phone } from 'lucide-react';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AdminPageLayout, FormTextArea, Section, Card, Button, Badge, StatGrid, StatCard } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  ViewAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';

const ClubManagement = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [adminEmails, setAdminEmails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    club_type: 'all'
  });
  const [showDeactivateModal, setShowDeactivateModal] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [showReactivateModal, setShowReactivateModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadClubs();
  }, [filters.status, filters.club_type]);

  const loadClubs = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {};
      if (filters.status !== 'all') filterParams.status = filters.status;
      if (filters.club_type !== 'all') filterParams.club_type = filters.club_type;

      const clubsData = await apiService.getClubs(filterParams);
      const clubsList = clubsData.clubs || [];
      setClubs(clubsList);

      // Fetch admin emails for all clubs
      const emailsMap = {};
      await Promise.all(
        clubsList.map(async (club) => {
          if (club.admin_user_id) {
            try {
              const adminUser = await apiService.getUser(club.admin_user_id);
              emailsMap[club.id] = adminUser.email;
            } catch (err) {
              console.warn(`Could not fetch admin email for club ${club.id}:`, err);
            }
          }
        })
      );
      setAdminEmails(emailsMap);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDeactivateClub = async (club_id) => {
    if (!deactivateReason.trim()) {
      showError('Please provide a reason for deactivation');
      return;
    }

    try {
      await apiService.deactivateClub(club_id, deactivateReason);
      showSuccess('Club deactivated successfully');
      setShowDeactivateModal(null);
      setDeactivateReason('');
      loadClubs(); // Refresh the list
    } catch (err) {
      showError(`Error deactivating club: ${err.message}`);
    }
  };

  const handleReactivateClub = async (club_id) => {
    try {
      await apiService.reactivateClub(club_id);
      showSuccess('Club reactivated successfully');
      setShowReactivateModal(null);
      loadClubs(); // Refresh the list
    } catch (err) {
      showError(`Error reactivating club: ${err.message}`);
    }
  };

  const handleDeleteClub = async (club_id, clubName) => {
    setIsDeleting(true);

    try {
      const result = await apiService.deleteClub(club_id);
      // The API may return null for 204 No Content. Show a generic success message.
      if (result && result.summary) {
        const summary = result.summary;
        const totalDeleted = summary.total_entities_deleted || 0;
        showSuccess(
          `Club "${clubName}" deleted permanently. ` +
          `${totalDeleted} total entities removed including ` +
          `${summary.deleted_entities.users || 0} users, ` +
          `${summary.deleted_entities.programs || 0} programs, ` +
          `${summary.deleted_entities.teams || 0} teams, and more.`
        );
      } else {
        showSuccess(`Club "${clubName}" deleted permanently.`);
      }
      setShowDeleteModal(null);
      setDeleteConfirmation('');
      // Optimistically remove the club from UI state
      setClubs(prevClubs => prevClubs.filter(c => c.id !== club_id));
      await loadClubs(); // Refresh the list from server
    } catch (err) {
      showError(`Error deleting club: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredClubs = clubs.filter(club => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      return (
        (club.legal_name && club.legal_name.toLowerCase().includes(searchTerm)) ||
        (club.display_name && club.display_name.toLowerCase().includes(searchTerm)) ||
        (club.contact_email && club.contact_email.toLowerCase().includes(searchTerm)) ||
        (club.email && club.email.toLowerCase().includes(searchTerm)) ||
        (club.city && club.city.toLowerCase().includes(searchTerm)) ||
        (club.state && club.state.toLowerCase().includes(searchTerm))
      );
    }
    return true;
  });

  const getClubIcon = (clubType) => {
    return <Building2 size={32} />;
  };

  const getClubIconVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'pending': return 'primary';
      default: return 'secondary';
    }
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => {
        setFilters(prev => ({ ...prev, search: val }));
      }}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'pending', label: 'Pending' }
          ]
        },
        {
          name: 'club_type',
          label: 'Club Type',
          options: [
            { value: 'all', label: 'All Types' },
            { value: 'youth_soccer', label: 'Youth Soccer' },
            { value: 'recreational', label: 'Recreational' },
            { value: 'competitive', label: 'Competitive' },
            { value: 'academy', label: 'Academy' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status, club_type: filters.club_type }}
      onFilterChange={(name, val) => {
        setFilters(prev => ({ ...prev, [name]: val }));
      }}
      onClearFilters={() => setFilters({ search: '', status: 'all', club_type: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={() => navigate('/admin/clubs/create')} label="New Club" />
      }
    />
  );

  const primaryActions = (
    <>
    </>
  );

  return (
    <AdminPageLayout
      title="Clubs Directory"
      subtitle="Comprehensive governance of all platform organizations"
      backPath="/admin/platform"
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
        {/* Statistics Summary */}
        <Section title="ECOSYSTEM PULSE">
          <StatGrid>
            <StatCard
              number={clubs.length}
              label="TOTAL ENTITIES"
              detail="Orchestrated organizations"
              icon={<Building2 size={24} />}
            />
            <StatCard
              number={clubs.filter(c => c.status === 'active').length}
              label="OPERATIONAL"
              detail="Serving members"
              icon={<Trophy size={24} />}
              variant="success"
            />
            <StatCard
              number={clubs.filter(c => c.status === 'pending').length}
              label="ONBOARDING"
              detail="In provisioning phase"
              icon={<Calendar size={24} />}
              variant="primary"
            />
          </StatGrid>
        </Section>

        <Section title="ORGANIZATION INDEX">
          <Listing
            loading={loading}
            items={filteredClubs}
            actions={headerActions}
            filters={filterBar}
            variant="list"
            renderItem={(club) => (
              <ListingItem
                key={club.id}
                title={club.display_name || club.legal_name}
                subtitle={club.legal_name}
                icon={getClubIcon(club.club_type)}
                iconVariant={getClubIconVariant(club.status)}
                actions={
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <ViewAction
                      size="xs"
                      onClick={() => navigate(`/admin/clubs/${club.id}`)}
                      title="Inspect Entity"
                    />
                    <EditAction
                      size="xs"
                      onClick={() => navigate(`/admin/clubs/${club.id}/edit`)}
                      title="Adjust Configuration"
                    />
                    <DeleteAction
                      size="xs"
                      onClick={() => setShowDeleteModal(club)}
                      title="Decommission Entity"
                    />
                  </div>
                }
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', marginTop: '1.25rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Primary Gateway
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <Mail size={14} style={{ opacity: 0.5 }} /> {club.contact_email || club.email}
                    </div>
                    {club.contact_phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                        <Phone size={14} style={{ opacity: 0.5 }} /> {club.contact_phone}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Operational Scale
                    </div>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.9rem' }}>
                      <span title="Coaches" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Users size={14} style={{ opacity: 0.5 }} /> <strong>{club.total_coaches || 0}</strong> Staff
                      </span>
                      <span title="Players" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Trophy size={14} style={{ opacity: 0.5 }} /> <strong>{club.total_players || 0}</strong> Members
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Regional Presence
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <MapPin size={14} style={{ opacity: 0.5 }} /> {club.city}, {club.state}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Infrastructure Status
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      {club.status === 'active' ? (
                        <Badge variant="success" size="sm">ACTIVE</Badge>
                      ) : (
                        <Badge variant="warning" size="sm">SUSPENDED</Badge>
                      )}

                      {club.status === 'active' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeactivateModal(club)}
                          style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning)' }}
                        >Deactivate</Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => setShowReactivateModal(club)}
                        >Reactivate</Button>
                      )}
                    </div>
                  </div>
                </div>

                {adminEmails[club.id] && (
                  <div style={{
                    marginTop: '1.25rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid var(--color-border-light)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <Badge variant="outline" size="sm">ADMINISTRATOR</Badge>
                    <code style={{ fontSize: '0.85rem', color: 'var(--color-info)' }}>{adminEmails[club.id]}</code>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--color-text-tertiary)', fontSize: '0.75rem' }}>
                      <span style={{ fontWeight: 600 }}>TENANT_ID:</span>
                      <code>{club.tenant_id}</code>
                    </div>
                  </div>
                )}
              </ListingItem>
            )}
            emptyMessage="No platform organizations identified matching these criteria"
          />
        </Section>
      </div>


      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => {
            setShowDeactivateModal(null);
            setDeactivateReason('');
          }}
          onConfirm={() => handleDeactivateClub(showDeactivateModal.id)}
          title="Suspend Organization Authority"
          variant="warning"
          confirmText="Confirm Suspension"
          disabled={!deactivateReason.trim()}
        >
          <p style={{ marginBottom: '1.5rem' }}>
            Are you sure you want to suspend access for <strong>{showDeactivateModal.display_name || showDeactivateModal.legal_name}</strong>?
          </p>
          <FormTextArea
            label="Strategic Reason for Suspension"
            name="deactivate-reason"
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            placeholder="Document the operational rationale for this suspension..."
            rows={3}
            required
          />
          <p style={{ marginTop: '1rem', opacity: 0.7, fontSize: '0.85rem' }}>
            This action will immediately disable all administrative and member access points for this tenant.
          </p>
        </ConfirmDialog>
      )}

      {/* Reactivate Modal */}
      {showReactivateModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowReactivateModal(null)}
          onConfirm={() => handleReactivateClub(showReactivateModal.id)}
          title="Restore Organization Authority"
          variant="info"
          confirmText="Initialise Restoration"
        >
          <p>
            Confirm restoration of full operational privileges for <strong>{showReactivateModal.display_name || showReactivateModal.legal_name}</strong>.
          </p>
          <p style={{ marginTop: '0.5rem', opacity: 0.7, fontSize: '0.9rem' }}>
            All linked identities and services will be automatically provisioned back to active status.
          </p>
        </ConfirmDialog>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => {
            setShowDeleteModal(null);
            setDeleteConfirmation('');
          }}
          onConfirm={() => handleDeleteClub(showDeleteModal.id, showDeleteModal.display_name || showDeleteModal.legal_name)}
          title="Permanent Entity Decommission"
          variant="danger"
          confirmText="Finalize Decommission"
          requireTextConfirmation
          confirmationText={showDeleteModal.display_name || showDeleteModal.legal_name}
          loading={isDeleting}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p>
              You are initiating a <strong>Permanent Decommission</strong> of <strong>{showDeleteModal.display_name || showDeleteModal.legal_name}</strong>. This process is irreversible.
            </p>
            <div style={{ backgroundColor: 'var(--color-error-light)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--color-error)' }}>
              <p style={{ fontWeight: '700', color: 'var(--color-error)', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                DESTRUCTIVE IMPACT ANALYSIS:
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.85rem', color: 'var(--color-error)', opacity: 0.9, lineHeight: '1.6' }}>
                <li>Purge all constituent users (Admins, Coaches, Parents)</li>
                <li>Erase all program structures and enrollment history</li>
                <li>Delete all team rosters and performance metrics</li>
                <li>Remove all associated documents, waivers and templates</li>
                <li>Terminate all scheduled events and calendar links</li>
              </ul>
            </div>
            <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Please type the organization name below to authorize final decommissioning.
            </p>
          </div>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

export default ClubManagement;